//==================================================================================================
// TrackingContainersTable
//
// Responsibilities:
// - Receives "trackingDetails" array (each item represents a container tracking in Ocean mode).
// - Renders a containers timeline table similar to Shipsgo:
//     Container | Empty to Shipper | Gate In | Gate Out | Empty Return
// - Reuses TrackingEventsTable CSS classes so the styling is identical.
//
// Notes:
// - Event mapping uses Shipsgo ocean event codes observed in your payload:
//     EMSH => Empty to Shipper
//     GTIN => Gate In
//     GTOT => Gate Out (if present)
//     EMRT => Empty Return (if present)
//==================================================================================================

import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { headerCellStyle, cellStyle as tdStyle } from "src/components/tableHelpers/TableStyleUtils";
import "./TrackingEventsTable.css"; // reuse same CSS

export default function TrackingContainersTable({ trackingDetails }) {
    const { t } = useTranslation();
    const rows = useMemo(() => {
        const arr = Array.isArray(trackingDetails) ? trackingDetails : [];

        return arr.map((t) => {
            const containerNo = String(t?.ExternalId ?? t?.Metadata?.container_number ?? t?.metadata?.container_number ?? "-");

            const size = getAny(t, ["Metadata.container_size", "metadata.container_size"]);
            const type = getAny(t, ["Metadata.container_type", "metadata.container_type"]);
            const badge = buildContainerBadge(size, type);

            const events = Array.isArray(t?.Events) ? t.Events : [];

            const emptyToShipper = pickEventDate(events, "EMSH");
            const gateIn = pickEventDate(events, "GTIN");
            const gateOut = pickEventDate(events, "GTOT");
            const emptyReturn = pickEventDate(events, "EMRT");

            return {
                key: containerNo,
                containerNo,
                badge,
                emptyToShipper,
                gateIn,
                gateOut,
                emptyReturn,
            };
        });
    }, [trackingDetails]);

    return (
        <div className="trackingEvents-wrapper">
            <div className="trackingEvents-table-container">
                <table className="trackingEvents">
                    <thead>
                        <tr>
                            <th style={headerCellStyle()} className="trackingEvents-header-cell col-container">
                                {t("Container")}
                            </th>
                            <th style={headerCellStyle()} className="trackingEvents-header-cell col-ets">
                                {t("EmptyToShipper")}
                            </th>
                            <th style={headerCellStyle()} className="trackingEvents-header-cell col-gatein">
                                {t("GateIn")}
                            </th>
                            <th style={headerCellStyle()} className="trackingEvents-header-cell col-gateout">
                                {t("GateOut")}
                            </th>
                            <th style={headerCellStyle()} className="trackingEvents-header-cell col-emptyreturn">
                                {t("EmptyReturn")}
                            </th>
                        </tr>
                    </thead>

                    <tbody>
                        {rows.length === 0 ? (
                            <tr>
                                <td className="trackingEvents-cell" style={tdStyle()} colSpan={5}>
                                    No data
                                </td>
                            </tr>
                        ) : (
                            rows.map((r) => (
                                <tr key={r.key}>
                                    <td className="trackingEvents-cell col-container" style={tdStyle()}>
                                        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                                            <span>{r.containerNo}</span>
                                            {r.badge && (
                                                <span
                                                    style={{
                                                        display: "inline-flex",
                                                        alignItems: "center",
                                                        height: 22,
                                                        padding: "0 8px",
                                                        borderRadius: 4,
                                                        fontSize: 12,
                                                        fontWeight: 600,
                                                        background: "rgba(0,0,0,0.55)",
                                                        color: "white",
                                                    }}
                                                >
                                                    {r.badge}
                                                </span>
                                            )}
                                        </div>
                                    </td>

                                    <td className="trackingEvents-cell col-ets" style={tdStyle()}>
                                        {r.emptyToShipper}
                                    </td>
                                    <td className="trackingEvents-cell col-gatein" style={tdStyle()}>
                                        {r.gateIn}
                                    </td>
                                    <td className="trackingEvents-cell col-gateout" style={tdStyle()}>
                                        {r.gateOut}
                                    </td>
                                    <td className="trackingEvents-cell col-emptyreturn" style={tdStyle()}>
                                        {r.emptyReturn}
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

TrackingContainersTable.propTypes = {
    /**
     * TrackingDetails array (Ocean). Each item is one container tracking object.
     */
    trackingDetails: PropTypes.array,
};

//==================================================================================================
// Private helpers
//==================================================================================================

function getMeta(ev, key) {
    if (!ev) return undefined;
    const a = ev?.Metadata?.[key];
    if (a !== undefined) return a;
    return ev?.metadata?.[key];
}

function safeTime(value) {
    if (!value) return 0;
    const d = new Date(value);
    const t = d.getTime();
    return Number.isNaN(t) ? 0 : t;
}

function fmtDateOnly(iso) {
    if (!iso) return "-";
    try {
        const d = new Date(iso);
        if (Number.isNaN(d.getTime())) return "-";
        // Shipsgo screenshot uses dd/MM/yyyy. Keep it stable (UTC) to avoid locale issues.
        const dd = String(d.getUTCDate()).padStart(2, "0");
        const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
        const yyyy = String(d.getUTCFullYear());
        return `${dd}/${mm}/${yyyy}`;
    } catch {
        return "-";
    }
}

/**
 * Picks the most recent event (by Timestamp) matching the event_code.
 */
function pickEventDate(events, eventCode) {
    const list = Array.isArray(events) ? events : [];
    const match = list
        .filter((e) => String(getMeta(e, "event_code") ?? "").toUpperCase() === String(eventCode).toUpperCase())
        .sort((a, b) => safeTime(b?.Timestamp) - safeTime(a?.Timestamp))[0];

    return match?.Timestamp ? fmtDateOnly(match.Timestamp) : "-";
}

/**
 * Reads nested values from object using paths like "Metadata.container_size"
 */
function getAny(obj, paths) {
    for (const p of paths) {
        const parts = String(p).split(".");
        let cur = obj;
        let ok = true;
        for (const part of parts) {
            if (cur && Object.prototype.hasOwnProperty.call(cur, part)) cur = cur[part];
            else {
                ok = false;
                break;
            }
        }
        if (ok && cur != null) return cur;
    }
    return null;
}

function buildContainerBadge(size, type) {
    const n = Number(size);
    const sz = Number.isFinite(n) && n > 0 ? String(n) : "";
    const tp = type ? String(type).trim() : "";
    if (!sz && !tp) return "";
    return `${sz || "?"} ${tp || ""}`.trim();
}